import React, { useState, useEffect } from 'react';
import { useTheme } from '../../contexts/ThemeContext';
import GlassCard from '../GlassCard';
import api, { familiesAPI, peopleAPI } from '../../services/api';

const MembersManagement = () => {
  const { theme } = useTheme();
  const [families, setFamilies] = useState([]);
  const [selectedFamily, setSelectedFamily] = useState(null);
  const [members, setMembers] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [editingId, setEditingId] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    loadFamilies();
    loadUsers();
  }, []);

  useEffect(() => {
    if (selectedFamily) {
      loadMembers(selectedFamily);
    } else {
      setMembers([]);
    }
  }, [selectedFamily]);

  const loadFamilies = async () => {
    try {
      const response = await familiesAPI.list();
      setFamilies(response.data.families || []);
      if (response.data.families?.length > 0 && !selectedFamily) {
        setSelectedFamily(response.data.families[0].id);
      }
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to load families');
    }
  };

  const loadUsers = async () => {
    try {
      const response = await api.get('/api/ft/admin/users');
      setUsers(response.data.users || []);
    } catch (err) {
      console.error('Failed to load users:', err);
      // Don't set error here as it's not critical for member management
    }
  };

  const loadMembers = async (familyId) => {
    try {
      setLoading(true);
      setError('');
      const response = await peopleAPI.list(familyId);
      setMembers(response.data.people || []);
    } catch (err) {
      console.error('Failed to load members:', err);
      setError(err.response?.data?.error || 'Failed to load members');
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (member) => {
    setEditingId(member.id);
    setEditForm({
      first_name: member.first_name || '',
      middle_name: member.middle_name || '',
      last_name: member.last_name || '',
      preferred_name: member.preferred_name || '',
      date_of_birth: member.date_of_birth || '',
      date_of_death: member.date_of_death || '',
      sex: member.sex || 'unknown',
      city: member.city || '',
      country: member.country || '',
      notes: member.notes || '',
      claimed_by_user_id: member.claimed_by_user_id || '',
    });
  };

  const handleSave = async (memberId) => {
    try {
      setError('');
      
      // Validate required fields
      if (!editForm.first_name || !editForm.first_name.trim()) {
        setError('First name is required');
        return;
      }
      
      // Prepare data for API
      // The API skips None values, so we only send fields with actual values
      // For date fields, empty strings cause errors, so we only send if there's a value
      const updateData = {};
      
      // Required field - always send
      updateData.first_name = editForm.first_name.trim();
      
      // Optional text fields - only send if they have values (empty string = don't update)
      if (editForm.middle_name && editForm.middle_name.trim()) {
        updateData.middle_name = editForm.middle_name.trim();
      }
      if (editForm.last_name && editForm.last_name.trim()) {
        updateData.last_name = editForm.last_name.trim();
      }
      if (editForm.preferred_name && editForm.preferred_name.trim()) {
        updateData.preferred_name = editForm.preferred_name.trim();
      }
      
      // Sex - always send
      updateData.sex = editForm.sex || 'unknown';
      
      // Date fields - only send if they have a value (empty string = don't send, API will skip)
      // Note: To clear a date, you'd need to send null, but API skips null values
      // So we only send dates that have values
      if (editForm.date_of_birth && editForm.date_of_birth.trim()) {
        updateData.date_of_birth = editForm.date_of_birth.trim();
      }
      if (editForm.date_of_death && editForm.date_of_death.trim()) {
        updateData.date_of_death = editForm.date_of_death.trim();
      }
      
      // Location fields - only send if they have values
      if (editForm.city && editForm.city.trim()) {
        updateData.city = editForm.city.trim();
      }
      if (editForm.country && editForm.country.trim()) {
        updateData.country = editForm.country.trim();
      }
      if (editForm.notes && editForm.notes.trim()) {
        updateData.notes = editForm.notes.trim();
      }
      
      // Claimed by user - send null if empty string, otherwise send the user ID
      if (editForm.claimed_by_user_id && editForm.claimed_by_user_id.trim()) {
        updateData.claimed_by_user_id = editForm.claimed_by_user_id.trim();
      } else {
        // Allow clearing the claim by sending null
        updateData.claimed_by_user_id = null;
      }
      
      await peopleAPI.update(memberId, updateData);
      setEditingId(null);
      setEditForm({});
      await loadMembers(selectedFamily);
    } catch (err) {
      console.error('Failed to update member:', err);
      setError(err.response?.data?.error || err.message || 'Failed to update member');
    }
  };

  const handleCancel = () => {
    setEditingId(null);
    setEditForm({});
    setError('');
  };

  const handleChange = (field, value) => {
    setEditForm({ ...editForm, [field]: value });
  };

  // Filter members based on search term
  const filteredMembers = members.filter((member) => {
    if (!searchTerm.trim()) return true;
    const searchLower = searchTerm.toLowerCase();
    const fullName = `${member.first_name || ''} ${member.middle_name || ''} ${member.last_name || ''} ${member.preferred_name || ''}`.toLowerCase();
    const city = (member.city || '').toLowerCase();
    const country = (member.country || '').toLowerCase();
    return fullName.includes(searchLower) || city.includes(searchLower) || country.includes(searchLower);
  });

  return (
    <div className="members-management-desktop-only">
      <div style={{ marginBottom: '24px' }}>
        <h2 style={{ fontSize: 'clamp(20px, 4vw, 24px)', margin: '0 0 16px 0', color: theme.text.primary }}>
          👥 Members Management
        </h2>
        <p style={{ color: theme.text.secondary, fontSize: '14px', marginBottom: '16px' }}>
          Select a family to manage its members. Edit member information directly in the table below.
        </p>
        
        {/* Family Selector */}
        <div style={{ marginBottom: '16px' }}>
          <label style={{ 
            display: 'block', 
            marginBottom: '8px', 
            color: theme.text.secondary, 
            fontSize: '14px',
            fontWeight: 500,
          }}>
            Select Family
          </label>
          <select
            value={selectedFamily || ''}
            onChange={(e) => setSelectedFamily(e.target.value)}
            style={{
              width: '100%',
              maxWidth: '400px',
              padding: '12px',
              borderRadius: '8px',
              border: theme.glass.border,
              background: theme.background,
              color: theme.text.primary,
              fontSize: '16px',
              minHeight: '44px',
              cursor: 'pointer',
            }}
          >
            <option value="">-- Select a family --</option>
            {families.map((family) => (
              <option key={family.id} value={family.id}>
                {family.name} ({family.member_count || 0} members)
              </option>
            ))}
          </select>
        </div>

        {selectedFamily && (
          <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap', alignItems: 'center' }}>
            <button
              onClick={() => loadMembers(selectedFamily)}
              style={{
                padding: '10px 16px',
                borderRadius: '8px',
                border: theme.glass.border,
                background: theme.card.background,
                color: theme.text.primary,
                cursor: 'pointer',
                fontSize: '14px',
                fontWeight: 500,
                minHeight: '44px',
              }}
            >
              🔄 Refresh Members
            </button>
          </div>
        )}

        {/* Search Filter */}
        {selectedFamily && members.length > 0 && (
          <div style={{ marginTop: '16px' }}>
            <label style={{ 
              display: 'block', 
              marginBottom: '8px', 
              color: theme.text.secondary, 
              fontSize: '14px',
              fontWeight: 500,
            }}>
              🔍 Search Members
            </label>
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Search by name, city, or country..."
              style={{
                width: '100%',
                maxWidth: '400px',
                padding: '12px',
                borderRadius: '8px',
                border: theme.glass.border,
                background: theme.background,
                color: theme.text.primary,
                fontSize: '16px',
                minHeight: '44px',
              }}
            />
            {searchTerm && (
              <div style={{ marginTop: '8px', fontSize: '14px', color: theme.text.secondary }}>
                Showing {filteredMembers.length} of {members.length} members
              </div>
            )}
          </div>
        )}
      </div>

      {error && (
        <div style={{
          padding: '12px',
          borderRadius: '8px',
          background: 'rgba(255, 0, 0, 0.1)',
          border: '1px solid rgba(255, 0, 0, 0.3)',
          color: '#ff4444',
          marginBottom: '16px',
          fontSize: '14px',
        }}>
          {error}
        </div>
      )}

      {loading ? (
        <div style={{ padding: '32px 0', textAlign: 'center' }}>
          <p style={{ color: theme.text.secondary }}>Loading members...</p>
        </div>
      ) : !selectedFamily ? (
        <GlassCard style={{ padding: '32px', textAlign: 'center' }}>
          <p style={{ color: theme.text.secondary }}>Please select a family to view and manage members.</p>
        </GlassCard>
      ) : members.length === 0 ? (
        <GlassCard style={{ padding: '32px', textAlign: 'center' }}>
          <p style={{ color: theme.text.secondary }}>No members found in this family.</p>
        </GlassCard>
      ) : (
        <GlassCard style={{ padding: '16px', overflowX: 'auto' }}>
          <table style={{ 
            width: '100%', 
            borderCollapse: 'collapse',
            minWidth: '1200px', // Ensure table doesn't get too cramped
          }}>
            <thead>
              <tr style={{ borderBottom: theme.glass.border }}>
                <th style={{ padding: '12px', textAlign: 'left', color: theme.text.secondary, fontSize: '14px', fontWeight: 600, whiteSpace: 'nowrap' }}>
                  First Name
                </th>
                <th style={{ padding: '12px', textAlign: 'left', color: theme.text.secondary, fontSize: '14px', fontWeight: 600, whiteSpace: 'nowrap' }}>
                  Middle Name
                </th>
                <th style={{ padding: '12px', textAlign: 'left', color: theme.text.secondary, fontSize: '14px', fontWeight: 600, whiteSpace: 'nowrap' }}>
                  Last Name
                </th>
                <th style={{ padding: '12px', textAlign: 'left', color: theme.text.secondary, fontSize: '14px', fontWeight: 600, whiteSpace: 'nowrap' }}>
                  Preferred Name
                </th>
                <th style={{ padding: '12px', textAlign: 'left', color: theme.text.secondary, fontSize: '14px', fontWeight: 600, whiteSpace: 'nowrap' }}>
                  Date of Birth
                </th>
                <th style={{ padding: '12px', textAlign: 'left', color: theme.text.secondary, fontSize: '14px', fontWeight: 600, whiteSpace: 'nowrap' }}>
                  Date of Death
                </th>
                <th style={{ padding: '12px', textAlign: 'left', color: theme.text.secondary, fontSize: '14px', fontWeight: 600, whiteSpace: 'nowrap' }}>
                  Sex
                </th>
                <th style={{ padding: '12px', textAlign: 'left', color: theme.text.secondary, fontSize: '14px', fontWeight: 600, whiteSpace: 'nowrap' }}>
                  City
                </th>
                <th style={{ padding: '12px', textAlign: 'left', color: theme.text.secondary, fontSize: '14px', fontWeight: 600, whiteSpace: 'nowrap' }}>
                  Country
                </th>
                <th style={{ padding: '12px', textAlign: 'left', color: theme.text.secondary, fontSize: '14px', fontWeight: 600, whiteSpace: 'nowrap' }}>
                  Actions
                </th>
              </tr>
            </thead>
            <tbody>
              {filteredMembers.length === 0 ? (
                <tr>
                  <td colSpan="11" style={{ padding: '32px', textAlign: 'center', color: theme.text.secondary }}>
                    {searchTerm ? `No members found matching "${searchTerm}"` : 'No members found'}
                  </td>
                </tr>
              ) : (
                filteredMembers.map((member) => (
                  <tr
                    key={member.id}
                  style={{
                    borderBottom: theme.glass.border,
                    transition: 'background 0.2s ease',
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.background = theme.card.background;
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.background = 'transparent';
                  }}
                >
                  {editingId === member.id ? (
                    <>
                      <td style={{ padding: '8px' }}>
                        <input
                          type="text"
                          value={editForm.first_name || ''}
                          onChange={(e) => handleChange('first_name', e.target.value)}
                          style={{
                            width: '100%',
                            padding: '8px',
                            borderRadius: '4px',
                            border: theme.glass.border,
                            background: theme.background,
                            color: theme.text.primary,
                            fontSize: '14px',
                          }}
                        />
                      </td>
                      <td style={{ padding: '8px' }}>
                        <input
                          type="text"
                          value={editForm.middle_name || ''}
                          onChange={(e) => handleChange('middle_name', e.target.value)}
                          style={{
                            width: '100%',
                            padding: '8px',
                            borderRadius: '4px',
                            border: theme.glass.border,
                            background: theme.background,
                            color: theme.text.primary,
                            fontSize: '14px',
                          }}
                        />
                      </td>
                      <td style={{ padding: '8px' }}>
                        <input
                          type="text"
                          value={editForm.last_name || ''}
                          onChange={(e) => handleChange('last_name', e.target.value)}
                          style={{
                            width: '100%',
                            padding: '8px',
                            borderRadius: '4px',
                            border: theme.glass.border,
                            background: theme.background,
                            color: theme.text.primary,
                            fontSize: '14px',
                          }}
                        />
                      </td>
                      <td style={{ padding: '8px' }}>
                        <input
                          type="text"
                          value={editForm.preferred_name || ''}
                          onChange={(e) => handleChange('preferred_name', e.target.value)}
                          style={{
                            width: '100%',
                            padding: '8px',
                            borderRadius: '4px',
                            border: theme.glass.border,
                            background: theme.background,
                            color: theme.text.primary,
                            fontSize: '14px',
                          }}
                        />
                      </td>
                      <td style={{ padding: '8px' }}>
                        <input
                          type="date"
                          value={editForm.date_of_birth || ''}
                          onChange={(e) => handleChange('date_of_birth', e.target.value || null)}
                          style={{
                            width: '100%',
                            padding: '8px',
                            borderRadius: '4px',
                            border: theme.glass.border,
                            background: theme.background,
                            color: theme.text.primary,
                            fontSize: '14px',
                          }}
                        />
                      </td>
                      <td style={{ padding: '8px' }}>
                        <input
                          type="date"
                          value={editForm.date_of_death || ''}
                          onChange={(e) => handleChange('date_of_death', e.target.value || null)}
                          style={{
                            width: '100%',
                            padding: '8px',
                            borderRadius: '4px',
                            border: theme.glass.border,
                            background: theme.background,
                            color: theme.text.primary,
                            fontSize: '14px',
                          }}
                        />
                      </td>
                      <td style={{ padding: '8px' }}>
                        <select
                          value={editForm.sex || 'unknown'}
                          onChange={(e) => handleChange('sex', e.target.value)}
                          style={{
                            width: '100%',
                            padding: '8px',
                            borderRadius: '4px',
                            border: theme.glass.border,
                            background: theme.background,
                            color: theme.text.primary,
                            fontSize: '14px',
                            cursor: 'pointer',
                          }}
                        >
                          <option value="unknown">Unknown</option>
                          <option value="female">Female</option>
                          <option value="male">Male</option>
                          <option value="intersex">Intersex</option>
                        </select>
                      </td>
                      <td style={{ padding: '8px' }}>
                        <input
                          type="text"
                          value={editForm.city || ''}
                          onChange={(e) => handleChange('city', e.target.value)}
                          style={{
                            width: '100%',
                            padding: '8px',
                            borderRadius: '4px',
                            border: theme.glass.border,
                            background: theme.background,
                            color: theme.text.primary,
                            fontSize: '14px',
                          }}
                        />
                      </td>
                      <td style={{ padding: '8px' }}>
                        <input
                          type="text"
                          value={editForm.country || ''}
                          onChange={(e) => handleChange('country', e.target.value)}
                          style={{
                            width: '100%',
                            padding: '8px',
                            borderRadius: '4px',
                            border: theme.glass.border,
                            background: theme.background,
                            color: theme.text.primary,
                            fontSize: '14px',
                          }}
                        />
                      </td>
                      <td style={{ padding: '8px' }}>
                        <select
                          value={editForm.claimed_by_user_id || ''}
                          onChange={(e) => handleChange('claimed_by_user_id', e.target.value)}
                          style={{
                            width: '100%',
                            padding: '8px',
                            borderRadius: '4px',
                            border: theme.glass.border,
                            background: theme.background,
                            color: theme.text.primary,
                            fontSize: '14px',
                            cursor: 'pointer',
                          }}
                        >
                          <option value="">-- Not Claimed --</option>
                          {users.map((user) => (
                            <option key={user.id} value={user.id}>
                              {user.full_name || user.email}
                            </option>
                          ))}
                        </select>
                      </td>
                      <td style={{ padding: '8px' }}>
                        <div style={{ display: 'flex', gap: '4px' }}>
                          <button
                            onClick={() => handleSave(member.id)}
                            style={{
                              padding: '6px 12px',
                              borderRadius: '4px',
                              border: 'none',
                              background: theme.accent.primary,
                              color: 'white',
                              cursor: 'pointer',
                              fontSize: '12px',
                              fontWeight: 600,
                            }}
                          >
                            ✓
                          </button>
                          <button
                            onClick={handleCancel}
                            style={{
                              padding: '6px 12px',
                              borderRadius: '4px',
                              border: theme.glass.border,
                              background: 'transparent',
                              color: theme.text.secondary,
                              cursor: 'pointer',
                              fontSize: '12px',
                              fontWeight: 600,
                            }}
                          >
                            ✕
                          </button>
                        </div>
                      </td>
                    </>
                  ) : (
                    <>
                      <td style={{ padding: '12px', color: theme.text.primary, fontSize: '14px' }}>
                        {member.first_name || '-'}
                      </td>
                      <td style={{ padding: '12px', color: theme.text.primary, fontSize: '14px' }}>
                        {member.middle_name || '-'}
                      </td>
                      <td style={{ padding: '12px', color: theme.text.primary, fontSize: '14px' }}>
                        {member.last_name || '-'}
                      </td>
                      <td style={{ padding: '12px', color: theme.text.primary, fontSize: '14px' }}>
                        {member.preferred_name || '-'}
                      </td>
                      <td style={{ padding: '12px', color: theme.text.secondary, fontSize: '14px' }}>
                        {member.date_of_birth ? new Date(member.date_of_birth).toLocaleDateString() : '-'}
                      </td>
                      <td style={{ padding: '12px', color: theme.text.secondary, fontSize: '14px' }}>
                        {member.date_of_death ? new Date(member.date_of_death).toLocaleDateString() : '-'}
                      </td>
                      <td style={{ padding: '12px', color: theme.text.secondary, fontSize: '14px', textTransform: 'capitalize' }}>
                        {member.sex || 'unknown'}
                      </td>
                      <td style={{ padding: '12px', color: theme.text.secondary, fontSize: '14px' }}>
                        {member.city || '-'}
                      </td>
                      <td style={{ padding: '12px', color: theme.text.secondary, fontSize: '14px' }}>
                        {member.country || '-'}
                      </td>
                      <td style={{ padding: '12px', color: theme.text.secondary, fontSize: '14px' }}>
                        {member.claimed_by_user_id ? (
                          (() => {
                            const claimedUser = users.find(u => u.id === member.claimed_by_user_id);
                            return claimedUser ? (claimedUser.full_name || claimedUser.email) : 'Unknown User';
                          })()
                        ) : '-'}
                      </td>
                      <td style={{ padding: '12px' }}>
                        <button
                          onClick={() => handleEdit(member)}
                          style={{
                            padding: '8px 12px',
                            borderRadius: '6px',
                            border: theme.glass.border,
                            background: 'transparent',
                            color: theme.text.secondary,
                            cursor: 'pointer',
                            fontSize: '14px',
                            minHeight: '44px',
                            minWidth: '44px',
                          }}
                          title="Edit member"
                        >
                          ✏️
                        </button>
                      </td>
                    </>
                  )}
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </GlassCard>
      )}
    </div>
  );
};

export default MembersManagement;
